import { getAuthToken } from '../../services/auth/tokenStorage';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import React, { useState, useEffect, useCallback } from 'react';
import { SafeAreaView, View, Image, TouchableOpacity, ScrollView, StyleSheet, StatusBar, Platform, Alert, Modal, Pressable } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../../config';
import { dashboardLogger } from '../../utils/logger';
import { analytics, AnalyticsEvent } from '../../services/analytics';
import { getCachedProfileImage, removeCachedProfileImage, setCachedProfileImage } from '../../services/profileImageCache';
import { vaultService } from '../../services/vaultService';
import { contactService } from '../../services/contactService';
import { expenseService } from '../../services/expenseService';
import { loyaltyPointService } from '../../services/loyaltyPointService';
import { documentStorageService } from '../../services/documentStorageService';
import { useNotifications } from '../../contexts/NotificationContext';
import { useUserSelection } from '../../contexts/UserSelectionContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import TrialBanner from '../../components/TrialBanner';
import WellnessCheckBanner from '../../components/WellnessCheckBanner';
import WellnessRequestCard from '../../components/WellnessRequestCard';
import { UpgradeBanner } from '../../components/UpgradeBanner';
import { SuccessorPromptBanner } from '../../components/SuccessorPromptBanner';
import { MigrationPromptBanner } from '../../components/MigrationPromptBanner';
import { PendingConnectionsBanner } from '../../components/PendingConnectionsBanner';
import { LettersQuickAccessCard } from '../../components/LettersQuickAccessCard';
import { FeedbackModal } from '../../components/FeedbackModal';
import { wipeSessionOnLogout } from '../../services/auth/logoutWipe';
import {
  FINANCE_TASKS_CONFIG,
  PERSONAL_TASKS_CONFIG,
  MEDICAL_TASKS_CONFIG,
  PROPERTY_TASKS_CONFIG,
  FINAL_WISHES_TASKS_CONFIG,
  FINAL_CARE_TASKS_CONFIG,
  HEALTHCARE_DIRECTIVES_TASKS_CONFIG,
  ESTATE_PLANNING_TASKS_CONFIG,
  CARE_PREFERENCES_TASKS_CONFIG,
  EXPENSES_TASKS_CONFIG,
  LOYALTY_POINTS_TASKS_CONFIG
} from '../../constants/taskIds';

// Aliased so every <Text> on this screen inherits the Dynamic Type cap
// without touching 38 call sites. See components/AppText.tsx.
import { AppText as Text } from '../../components/AppText';
import { ms, s, vs } from '../../utils/responsive';
import bellIcon from '../../assets/icons/notifications.png';
import logo from '../../assets/logo.png';
import logoOrange from '../../assets/logo-orange.png';
import logoWhite from '../../assets/logo_white.png';
import userIcon from '../../assets/icons/user.png';
import tasksIcon from '../../assets/icons/tasks.png';
import docsIcon from '../../assets/icons/docs.png';
import dashboardIcon from '../../assets/icons/dashboard.png';
import feedbackIcon from '../../assets/icons/communication.png';
import logoutIcon from '../../assets/icons/logout.png';
import defaultProfileImage from '../../assets/icons/profile.png';

interface DashboardScreenProps {
  navigation: NavigationProp<any>;
}

const DashboardScreen: React.FC<DashboardScreenProps> = ({ navigation }) => {
  const { unreadCount, refreshUnreadCount } = useNotifications();
  const { currentUserId, selectedUserId, isViewingOthersData, availablePrimaryUsers, setSelectedUserId, refreshPrimaryUsers, isInitialized } = useUserSelection();
  const { canViewOwnData, refreshSubscription } = useSubscription();

  // State for task completion data
  const [taskCounts, setTaskCounts] = useState({
    personal: { total: PERSONAL_TASKS_CONFIG.length, done: 0 },
    medical: { total: MEDICAL_TASKS_CONFIG.length, done: 0 },
    finances: { total: FINANCE_TASKS_CONFIG.length, done: 0 },
    property: { total: PROPERTY_TASKS_CONFIG.length, done: 0 },
    finalWishes: { total: FINAL_WISHES_TASKS_CONFIG.length, done: 0 },
    finalCare: { total: FINAL_CARE_TASKS_CONFIG.length, done: 0 },
    healthcareDirectives: { total: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length, done: 0 },
    estatePlanning: { total: ESTATE_PLANNING_TASKS_CONFIG.length, done: 0 },
    carePreferences: { total: CARE_PREFERENCES_TASKS_CONFIG.length, done: 0 },
    expenses: { total: EXPENSES_TASKS_CONFIG.length, done: 0 },
    loyaltyPoints: { total: LOYALTY_POINTS_TASKS_CONFIG.length, done: 0 },
    documents: { total: 8, done: 0 }, // 8 document categories
  });

  // Use config lengths directly to ensure correct totals (avoids potential state timing issues)
  const checklists = [
    { id: 'personal', title: 'Personal', todo: PERSONAL_TASKS_CONFIG.length, done: taskCounts.personal.done },
    { id: 'medical', title: 'Medical', todo: MEDICAL_TASKS_CONFIG.length, done: taskCounts.medical.done },
    { id: 'finances', title: 'Finances', todo: FINANCE_TASKS_CONFIG.length, done: taskCounts.finances.done },
    { id: 'property', title: 'Property', todo: PROPERTY_TASKS_CONFIG.length, done: taskCounts.property.done },
    { id: 'finalWishes', title: 'Final Wishes', todo: FINAL_WISHES_TASKS_CONFIG.length, done: taskCounts.finalWishes.done },
    { id: 'healthcareDirectives', title: 'Healthcare Directives', todo: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length, done: taskCounts.healthcareDirectives.done },
    { id: 'estatePlanning', title: 'Estate Planning', todo: ESTATE_PLANNING_TASKS_CONFIG.length, done: taskCounts.estatePlanning.done },
    { id: 'carePreferences', title: 'Care Preferences', todo: CARE_PREFERENCES_TASKS_CONFIG.length, done: taskCounts.carePreferences.done },
    { id: 'passwords', title: 'Passwords', todo: 0, done: 0 }, // Special handling
    { id: 'contacts', title: 'Contacts', todo: 0, done: 0 }, // Special handling
    { id: 'expenses', title: 'Expenses', todo: EXPENSES_TASKS_CONFIG.length, done: taskCounts.expenses.done },
    { id: 'documents', title: 'Documents', todo: 8, done: taskCounts.documents.done },
    { id: 'loyaltyPoints', title: 'Loyalty Points', todo: LOYALTY_POINTS_TASKS_CONFIG.length, done: taskCounts.loyaltyPoints.done },
    {
      id: 'allTasks',
      title: 'All Tasks',
      todo: PERSONAL_TASKS_CONFIG.length + MEDICAL_TASKS_CONFIG.length + FINANCE_TASKS_CONFIG.length +
            PROPERTY_TASKS_CONFIG.length + FINAL_WISHES_TASKS_CONFIG.length + HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length +
            ESTATE_PLANNING_TASKS_CONFIG.length + CARE_PREFERENCES_TASKS_CONFIG.length + EXPENSES_TASKS_CONFIG.length +
            8 + LOYALTY_POINTS_TASKS_CONFIG.length,
      done: Object.values(taskCounts).reduce((sum, cat) => sum + cat.done, 0)
    },
  ];

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Logout',
          style: 'destructive',
          onPress: async () => {
            try {
              try {
                const { pushNotificationService } = require('../../services/pushNotificationService');
                await pushNotificationService.removeToken();
              } catch (e) {
                dashboardLogger.debug('Push token removal on logout failed (non-fatal):', e);
              }

              await wipeSessionOnLogout();
              await AsyncStorage.setItem('@email_verified', 'true');
              await AsyncStorage.setItem('@2fa_setup_complete', 'true');

              analytics.reset();

              navigation.reset({
                index: 0,
                routes: [{
                  name: 'Auth',
                  state: { routes: [{ name: 'Login' }] },
                }],
              } as any);
            } catch (error) {
              dashboardLogger.error('Error during logout:', error);
              Alert.alert('Error', 'Failed to logout properly');
            }
          },
        },
      ]
    );
  };
  const [userProfile, setUserProfile] = useState<{ email: string; firstName: string; lastName: string } | null>(null);
  const [profileImageUri, setProfileImageUri] = useState<string | null>(null);
  const [passwordCount, setPasswordCount] = useState(0);
  const [contactCount, setContactCount] = useState(0);
  const [isDashboardSwitcherOpen, setIsDashboardSwitcherOpen] = useState(false);

  const selectedPrimaryUser = availablePrimaryUsers.find(user => user.id === selectedUserId);
  const ownDashboardName = userProfile
    ? `${userProfile.firstName ?? ''} ${userProfile.lastName ?? ''}`.trim()
    : '';
  const selectedDashboardTitle = selectedPrimaryUser
    ? `${selectedPrimaryUser.name}'s Dashboard`
    : 'My Dashboard';
  const selectedDashboardSubtitle = selectedPrimaryUser
    ? `Viewing as ${selectedPrimaryUser.role === 'successor' ? 'Successor' : 'Key Holder'}`
    : ownDashboardName || userProfile?.email || 'Viewing your own information';

  const selectDashboardUser = (userId: string) => {
    dashboardLogger.debug('Dashboard switcher changed to:', userId, '(current user:', currentUserId, ', selected:', selectedUserId, ')');
    if (userId && userId.length > 0) {
      setSelectedUserId(userId);
    }
    setIsDashboardSwitcherOpen(false);
  };

  const loadProfileImage = async () => {
    try {
      const token = await getAuthToken();

      // Try to load from backend first
      if (token) {
        try {
          const response = await fetch(`${API_URL}/users/profile-image`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.imageData) {
              await setCachedProfileImage(data.imageData);
              setProfileImageUri(data.imageData);
              return;
            }

            if (data.success) {
              await removeCachedProfileImage();
              setProfileImageUri(null);
              return;
            }
          }
        } catch (backendError) {
          dashboardLogger.debug('Could not load profile image from backend, trying local cache:', backendError);
        }
      }

      const savedImageUri = await getCachedProfileImage();
      setProfileImageUri(savedImageUri);
    } catch (error) {
      dashboardLogger.error('Error loading profile image:', error);
    }
  };

  useEffect(() => {
    // Fire first_dashboard_view once per user
    (async () => {
      const seen = await AsyncStorage.getItem('@analytics_first_dashboard_seen');
      if (!seen) {
        analytics.track(AnalyticsEvent.FirstDashboardView);
        await AsyncStorage.setItem('@analytics_first_dashboard_seen', '1');
      }
    })();

    // Refresh primary users when screen loads
    refreshPrimaryUsers();

    const fetchUserProfile = async () => {
      const token = await getAuthToken();
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/users/profile`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch profile');
        const result = await res.json();
        dashboardLogger.debug('User profile data:', result);

        // Extract user data from the response (same structure as ProfileScreen)
        if (result.user) {
          setUserProfile({
            email: result.user.email || '',
            firstName: result.user.firstName || '',
            lastName: result.user.lastName || '',
          });
        } else {
          // Fallback: if response structure is different, try using the result directly
          setUserProfile(result);
        }
      } catch (err) {
        dashboardLogger.error('Failed to fetch user profile from API:', err);
        // Fallback to AsyncStorage data
        try {
          const userDataString = await AsyncStorage.getItem('user');
          if (userDataString) {
            const userData = JSON.parse(userDataString);
            setUserProfile({
              email: userData.email || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
            });
          } else {
            setUserProfile(null);
          }
        } catch (storageErr) {
          dashboardLogger.error('Failed to get user data from AsyncStorage:', storageErr);
          setUserProfile(null);
        }
      }
    };

    const fetchTaskCounts = async (targetUserId?: string) => {
      const token = await getAuthToken();
      if (!token) return;

      try {
        // Fetch completed task IDs from backend (optionally for a different user)
        const url = targetUserId
          ? `${API_URL}/api/tasks/user/completed-tasks?targetUserId=${targetUserId}`
          : `${API_URL}/api/tasks/user/completed-tasks`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          const completedTaskIds = (data.completedTaskIds || []).map((id: any) => Number(id));
          dashboardLogger.debug('Fetched completed task IDs:', completedTaskIds);

          // Calculate completed tasks for each category
          const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
          const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
          const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
          const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
          const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
          const finalCareIds = new Set(FINAL_CARE_TASKS_CONFIG.map(t => Number(t.id)));
          const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
          const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
          const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));

          // For expenses and loyalty points, determine completion based on actual data
          let expensesDone = 0;
          let loyaltyPointsDone = 0;

          try {
            const allExpenses = await expenseService.getAllExpenses();
            const completedExpenseTasks: string[] = [];
            EXPENSES_TASKS_CONFIG.forEach(task => {
              if (task.category) {
                const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
                if (categoryExpenses.length > 0) {
                  completedExpenseTasks.push(task.id.toString());
                }
              }
            });
            expensesDone = completedExpenseTasks.length;
          } catch (error) {
            dashboardLogger.error('Error fetching expenses completion:', error);
          }

          try {
            const allLoyaltyPoints = await loyaltyPointService.getAllLoyaltyPoints(undefined, { quiet: true });
            const completedLoyaltyPointTasks: string[] = [];
            LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
              if (task.category) {
                const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
                if (categoryLoyaltyPoints.length > 0) {
                  completedLoyaltyPointTasks.push(task.id.toString());
                }
              }
            });
            loyaltyPointsDone = completedLoyaltyPointTasks.length;
          } catch (error) {
            dashboardLogger.error('Error fetching loyalty points completion:', error);
          }

          // Fetch document completion based on actual document data
          let documentsDone = 0;
          try {
            const allDocuments = await documentStorageService.listDocuments();
            const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
            const completedDocumentTasks: string[] = [];
            DOCUMENT_CATEGORIES.forEach((category) => {
              const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
              if (categoryDocuments.length > 0) {
                completedDocumentTasks.push(category);
              }
            });
            documentsDone = completedDocumentTasks.length;
          } catch (error) {
            dashboardLogger.error('Error fetching documents completion:', error);
          }

          setTaskCounts({
            personal: {
              total: PERSONAL_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => personalIds.has(id)).length
            },
            medical: {
              total: MEDICAL_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => medicalIds.has(id)).length
            },
            finances: {
              total: FINANCE_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => financeIds.has(id)).length
            },
            property: {
              total: PROPERTY_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => propertyIds.has(id)).length
            },
            finalWishes: {
              total: FINAL_WISHES_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => finalWishesIds.has(id)).length
            },
            finalCare: {
              total: FINAL_CARE_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => finalCareIds.has(id)).length
            },
            healthcareDirectives: {
              total: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => healthcareDirectivesIds.has(id)).length
            },
            estatePlanning: {
              total: ESTATE_PLANNING_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => estatePlanningIds.has(id)).length
            },
            carePreferences: {
              total: CARE_PREFERENCES_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => carePreferencesIds.has(id)).length
            },
            expenses: {
              total: EXPENSES_TASKS_CONFIG.length,
              done: expensesDone
            },
            loyaltyPoints: {
              total: LOYALTY_POINTS_TASKS_CONFIG.length,
              done: loyaltyPointsDone
            },
            documents: {
              total: 8,
              done: documentsDone
            },
          });
        }
      } catch (err) {
        dashboardLogger.error('Error fetching task counts:', err);
      }
    };

    const fetchPasswordCount = async () => {
      try {
        const passwords = await vaultService.getAllPasswords(undefined, { quiet: true });
        setPasswordCount(passwords.length);
      } catch (err) {
        dashboardLogger.error('Error fetching password count:', err);
        setPasswordCount(0);
      }
    };

    const fetchContactCount = async () => {
      try {
        // Fetch real contact count from contactService
        const contacts = await contactService.getAllContacts();
        const userContactCount = contacts.length;
        dashboardLogger.debug('Fetched contact count:', userContactCount);
        setContactCount(userContactCount);
      } catch (err) {
        dashboardLogger.error('Error fetching contact count:', err);
        setContactCount(0);
      }
    };

    fetchUserProfile();
    loadProfileImage();
    fetchTaskCounts();
    fetchPasswordCount();
    fetchContactCount();
  }, []);

  // Refetch task counts when selectedUserId changes
  useEffect(() => {
    const refetchForSelectedUser = async () => {
      if (!selectedUserId) return;

      const token = await getAuthToken();
      if (!token) return;

      dashboardLogger.debug('Fetching task counts for user:', selectedUserId, isViewingOthersData ? '(viewing other user)' : '(own data)');

      try {
        const url = isViewingOthersData
          ? `${API_URL}/api/tasks/user/completed-tasks?targetUserId=${selectedUserId}`
          : `${API_URL}/api/tasks/user/completed-tasks`;

        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          const completedTaskIds = (data.completedTaskIds || []).map((id: any) => Number(id));
          dashboardLogger.debug('Completed task IDs for selected user:', completedTaskIds);

          const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
          const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
          const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
          const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
          const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
          const finalCareIds = new Set(FINAL_CARE_TASKS_CONFIG.map(t => Number(t.id)));
          const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
          const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
          const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));

          // Fetch expenses completion
          let expensesDone = 0;
          try {
            const allExpenses = await expenseService.getAllExpenses();
            const completedExpenseTasks: string[] = [];
            EXPENSES_TASKS_CONFIG.forEach(task => {
              if (task.category) {
                const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
                if (categoryExpenses.length > 0) {
                  completedExpenseTasks.push(task.id.toString());
                }
              }
            });
            expensesDone = completedExpenseTasks.length;
          } catch (error) {
            dashboardLogger.error('Error fetching expenses for selected user:', error);
          }

          // Fetch loyalty points completion
          let loyaltyPointsDone = 0;
          try {
            const allLoyaltyPoints = await loyaltyPointService.getAllLoyaltyPoints(undefined, { quiet: true });
            const completedLoyaltyPointTasks: string[] = [];
            LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
              if (task.category) {
                const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
                if (categoryLoyaltyPoints.length > 0) {
                  completedLoyaltyPointTasks.push(task.id.toString());
                }
              }
            });
            loyaltyPointsDone = completedLoyaltyPointTasks.length;
          } catch (error) {
            dashboardLogger.error('Error fetching loyalty points for selected user:', error);
          }

          // Fetch document completion
          let documentsDone = 0;
          try {
            const allDocuments = await documentStorageService.listDocuments();
            const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
            const completedDocumentTasks: string[] = [];
            DOCUMENT_CATEGORIES.forEach((category) => {
              const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
              if (categoryDocuments.length > 0) {
                completedDocumentTasks.push(category);
              }
            });
            documentsDone = completedDocumentTasks.length;
          } catch (error) {
            dashboardLogger.error('Error fetching documents for selected user:', error);
          }

          setTaskCounts({
            personal: {
              total: PERSONAL_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => personalIds.has(id)).length
            },
            medical: {
              total: MEDICAL_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => medicalIds.has(id)).length
            },
            finances: {
              total: FINANCE_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => financeIds.has(id)).length
            },
            property: {
              total: PROPERTY_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => propertyIds.has(id)).length
            },
            finalWishes: {
              total: FINAL_WISHES_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => finalWishesIds.has(id)).length
            },
            finalCare: {
              total: FINAL_CARE_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => finalCareIds.has(id)).length
            },
            healthcareDirectives: {
              total: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => healthcareDirectivesIds.has(id)).length
            },
            estatePlanning: {
              total: ESTATE_PLANNING_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => estatePlanningIds.has(id)).length
            },
            carePreferences: {
              total: CARE_PREFERENCES_TASKS_CONFIG.length,
              done: completedTaskIds.filter((id: number) => carePreferencesIds.has(id)).length
            },
            expenses: {
              total: EXPENSES_TASKS_CONFIG.length,
              done: expensesDone
            },
            loyaltyPoints: {
              total: LOYALTY_POINTS_TASKS_CONFIG.length,
              done: loyaltyPointsDone
            },
            documents: {
              total: 8,
              done: documentsDone
            },
          });
        }
      } catch (err) {
        dashboardLogger.error('Error fetching task counts for selected user:', err);
      }
    };

    refetchForSelectedUser();
  }, [selectedUserId, isViewingOthersData]);

  // Refresh all counts when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      const fetchPasswordCount = async () => {
        try {
          const passwords = await vaultService.getAllPasswords(undefined, { quiet: true });
          setPasswordCount(passwords.length);
        } catch (err) {
          dashboardLogger.error('Error fetching password count:', err);
          setPasswordCount(0);
        }
      };

      const fetchContactCount = async () => {
        try {
          const contacts = await contactService.getAllContacts();
          const userContactCount = contacts.length;
          dashboardLogger.debug('Focus effect fetched contact count:', userContactCount);
          setContactCount(userContactCount);
        } catch (err) {
          dashboardLogger.error('Error fetching contact count:', err);
          setContactCount(0);
        }
      };

      const refreshTaskCounts = async () => {
        const token = await getAuthToken();
        if (!token) return;

        try {
          const res = await fetch(`${API_URL}/api/tasks/user/completed-tasks`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });

          if (res.ok) {
            const data = await res.json();
            const completedTaskIds = (data.completedTaskIds || []).map((id: any) => Number(id));

            const personalIds = new Set(PERSONAL_TASKS_CONFIG.map(t => Number(t.id)));
            const medicalIds = new Set(MEDICAL_TASKS_CONFIG.map(t => Number(t.id)));
            const financeIds = new Set(FINANCE_TASKS_CONFIG.map(t => Number(t.id)));
            const propertyIds = new Set(PROPERTY_TASKS_CONFIG.map(t => Number(t.id)));
            const finalWishesIds = new Set(FINAL_WISHES_TASKS_CONFIG.map(t => Number(t.id)));
            const finalCareIds = new Set(FINAL_CARE_TASKS_CONFIG.map(t => Number(t.id)));
            const healthcareDirectivesIds = new Set(HEALTHCARE_DIRECTIVES_TASKS_CONFIG.map(t => Number(t.id)));
            const estatePlanningIds = new Set(ESTATE_PLANNING_TASKS_CONFIG.map(t => Number(t.id)));
            const carePreferencesIds = new Set(CARE_PREFERENCES_TASKS_CONFIG.map(t => Number(t.id)));

            // Fetch expenses completion
            let expensesDone = 0;
            try {
              const allExpenses = await expenseService.getAllExpenses();
              const completedExpenseTasks: string[] = [];
              EXPENSES_TASKS_CONFIG.forEach(task => {
                if (task.category) {
                  const categoryExpenses = allExpenses.filter(e => e.category.toLowerCase() === task.category.toLowerCase());
                  if (categoryExpenses.length > 0) {
                    completedExpenseTasks.push(task.id.toString());
                  }
                }
              });
              expensesDone = completedExpenseTasks.length;
            } catch (error) {
              dashboardLogger.error('Error refreshing expenses completion:', error);
            }

            // Fetch loyalty points completion
            let loyaltyPointsDone = 0;
            try {
              const allLoyaltyPoints = await loyaltyPointService.getAllLoyaltyPoints(undefined, { quiet: true });
              const completedLoyaltyPointTasks: string[] = [];
              LOYALTY_POINTS_TASKS_CONFIG.forEach(task => {
                if (task.category) {
                  const categoryLoyaltyPoints = allLoyaltyPoints.filter(lp => lp.category.toLowerCase() === task.category.toLowerCase());
                  if (categoryLoyaltyPoints.length > 0) {
                    completedLoyaltyPointTasks.push(task.id.toString());
                  }
                }
              });
              loyaltyPointsDone = completedLoyaltyPointTasks.length;
            } catch (error) {
              dashboardLogger.error('Error refreshing loyalty points completion:', error);
            }

            // Fetch document completion
            let documentsDone = 0;
            try {
              const allDocuments = await documentStorageService.listDocuments();
              const DOCUMENT_CATEGORIES = ['personal', 'legal', 'medical', 'financial', 'property', 'insurance', 'digital', 'other'];
              const completedDocumentTasks: string[] = [];
              DOCUMENT_CATEGORIES.forEach((category) => {
                const categoryDocuments = allDocuments.filter(doc => doc.category?.toLowerCase() === category);
                if (categoryDocuments.length > 0) {
                  completedDocumentTasks.push(category);
                }
              });
              documentsDone = completedDocumentTasks.length;
            } catch (error) {
              dashboardLogger.error('Error refreshing documents completion:', error);
            }

            setTaskCounts(prev => ({
              ...prev,
              personal: { total: PERSONAL_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => personalIds.has(id)).length },
              medical: { total: MEDICAL_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => medicalIds.has(id)).length },
              finances: { total: FINANCE_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => financeIds.has(id)).length },
              property: { total: PROPERTY_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => propertyIds.has(id)).length },
              finalWishes: { total: FINAL_WISHES_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => finalWishesIds.has(id)).length },
              finalCare: { total: FINAL_CARE_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => finalCareIds.has(id)).length },
              healthcareDirectives: { total: HEALTHCARE_DIRECTIVES_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => healthcareDirectivesIds.has(id)).length },
              estatePlanning: { total: ESTATE_PLANNING_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => estatePlanningIds.has(id)).length },
              carePreferences: { total: CARE_PREFERENCES_TASKS_CONFIG.length, done: completedTaskIds.filter((id: number) => carePreferencesIds.has(id)).length },
              expenses: { total: EXPENSES_TASKS_CONFIG.length, done: expensesDone },
              loyaltyPoints: { total: LOYALTY_POINTS_TASKS_CONFIG.length, done: loyaltyPointsDone },
              documents: { total: 8, done: documentsDone },
            }));
          }
        } catch (err) {
          dashboardLogger.error('Error refreshing task counts on focus:', err);
        }
      };

      fetchPasswordCount();
      fetchContactCount();
      loadProfileImage();
      refreshTaskCounts();
    }, [])
  );

  // Helper to get button text and style
  const getButtonProps = (todo: number, done: number, checklistId?: string) => {
    // When viewing primary user's data, always show "View Data" button in teal
    if (isViewingOthersData) {
      return { text: 'View Data', style: styles.viewDataButton, textStyle: styles.viewDataButtonText };
    }

    // Special handling for passwords card
    if (checklistId === 'passwords') {
      if (passwordCount === 0) {
        return { text: 'Get Started', style: styles.getStartedButton, textStyle: styles.getStartedButtonText };
      } else {
        return { text: 'View Passwords', style: styles.reviewButton, textStyle: styles.reviewButtonText };
      }
    }

    // Special handling for contacts card
    if (checklistId === 'contacts') {
      if (contactCount === 0) {
        return { text: 'Get Started', style: styles.getStartedButton, textStyle: styles.getStartedButtonText };
      } else {
        return { text: 'View Contacts', style: styles.reviewButton, textStyle: styles.reviewButtonText };
      }
    }

    // Default logic for other cards
    if (done === 0) {
      // Not started
      return { text: 'Get Started', style: styles.getStartedButton, textStyle: styles.getStartedButtonText };
    } else if (done > 0 && done < todo) {
      // In progress
      return { text: 'Continue', style: styles.continueButton, textStyle: styles.continueButtonText };
    } else if (done === todo && todo > 0) {
      // Completed
      return { text: 'Review', style: styles.reviewButton, textStyle: styles.reviewButtonText };
    } else {
      return { text: 'Get Started', style: styles.getStartedButton, textStyle: styles.getStartedButtonText };
    }
  };

  const handleCardPress = (checklistId: string) => {
    switch (checklistId) {
      case 'personal':
        navigation.navigate('PersonalChecklist');
        break;
      case 'medical':
        navigation.navigate('MedicalChecklist');
        break;
      case 'finances':
        navigation.navigate('FinanceChecklist');
        break;
      case 'property':
        navigation.navigate('PropertyChecklist');
        break;
      case 'finalWishes':
        navigation.navigate('FinalWishesChecklist');
        break;
      case 'finalCare':
        navigation.navigate('FinalCareChecklist');
        break;
      case 'healthcareDirectives':
        navigation.navigate('HealthcareDirectivesChecklist');
        break;
      case 'estatePlanning':
        navigation.navigate('EstatePlanningChecklist');
        break;
      case 'carePreferences':
        navigation.navigate('CarePreferencesChecklist');
        break;
      case 'passwords':
        if (passwordCount === 0) {
          // No passwords yet - go to intro screen
          navigation.navigate('Passwords', { screen: 'PasswordsIntroScreen' });
        } else {
          // Has passwords - go to OnlineAccountsChecklistScreen
          navigation.navigate('OnlineAccountsChecklist');
        }
        break;
      case 'contacts':
        if (isViewingOthersData) {
          // Viewing another user's data - go directly to checklist with params
          navigation.navigate('EmergencyContactsChecklist');
        } else if (contactCount === 0) {
          // No contacts yet - go to intro screen
          navigation.navigate('Contacts', { screen: 'ContactsIntroScreen' });
        } else {
          // Has contacts - go to EmergencyContactsChecklistScreen
          navigation.navigate('EmergencyContactsChecklist');
        }
        break;
      case 'expenses':
        navigation.navigate('ExpensesChecklist');
        break;
      case 'documents':
        navigation.navigate('DocumentManagementChecklist');
        break;
      case 'loyaltyPoints':
        navigation.navigate('LoyaltyPointsChecklist');
        break;
      case 'allTasks':
        navigation.navigate('AllTasks');
        break;
      default:
        break;
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor="#000000" />
      <TrialBanner />
      <WellnessCheckBanner />
      <UpgradeBanner variant="compact" />
      <MigrationPromptBanner />
      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={{ paddingBottom: ms(140) }}
        >
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.hamburgerButton}
            onPress={() => setMenuOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Open navigation menu"
            accessibilityHint="Double tap to open the side menu"
          >
            <View style={styles.hamburgerBar} />
            <View style={styles.hamburgerBar} />
            <View style={styles.hamburgerBar} />
          </TouchableOpacity>
          <View style={styles.logoRow}>
            <View style={styles.logoContainer}>
              <Image source={logo} style={styles.logo} resizeMode="contain" />
            </View>
            <TouchableOpacity
              style={styles.notificationButton}
              onPress={() => navigation.navigate('Profile', { screen: 'Notifications' })}
              accessibilityRole="button"
              accessibilityLabel={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
              accessibilityHint="Double tap to view notifications"
            >
              <Image source={bellIcon} style={styles.bellIcon} accessibilityElementsHidden={true} />
              {unreadCount > 0 && (
                <View style={styles.badgeContainer} accessibilityElementsHidden={true}>
                  <Text style={styles.badgeText}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </Text>
                </View>
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.welcomeSection}>
            <View style={styles.profileSection}>
              <TouchableOpacity
                style={styles.profileImageContainer}
                onPress={() => navigation.navigate('Profile')}
                accessibilityRole="button"
                accessibilityLabel="Profile picture"
                accessibilityHint="Double tap to view your profile"
              >
                {profileImageUri ? (
                  <Image source={{ uri: profileImageUri }} style={styles.profileImage} accessibilityElementsHidden={true} />
                ) : (
                  <View style={styles.profileImagePlaceholder}>
                    <Image
                      source={userIcon}
                      style={styles.profileImageIcon}
                      resizeMode="contain"
                      accessibilityElementsHidden={true}
                    />
                  </View>
                )}
              </TouchableOpacity>
              <View style={styles.welcomeText}>
                <Text style={styles.welcomeBack}>Welcome Back,</Text>
                <TouchableOpacity
                  onPress={() => navigation.navigate('Profile')}
                  accessibilityRole="button"
                  accessibilityLabel={userProfile ? `${userProfile.firstName ?? ''} ${userProfile.lastName ?? ''}`.trim() || 'Complete Profile' : 'Loading'}
                  accessibilityHint="Double tap to view your profile"
                >
                  <Text style={styles.userName}>{userProfile ? `${userProfile.firstName ?? ''} ${userProfile.lastName ?? ''}`.trim() || 'Complete Profile' : 'Loading...'}</Text>
                </TouchableOpacity>
              </View>
            </View>
            {availablePrimaryUsers.length > 0 && isInitialized && currentUserId && (
              <View style={styles.dashboardSwitcherSection}>
                <Text style={styles.dashboardSwitcherLabel}>Dashboard View</Text>
                <TouchableOpacity
                  style={styles.dashboardSwitcherButton}
                  onPress={() => setIsDashboardSwitcherOpen(true)}
                  activeOpacity={0.8}
                  accessibilityRole="button"
                  accessibilityLabel={`Dashboard View. ${selectedDashboardTitle}. ${selectedDashboardSubtitle}`}
                  accessibilityHint="Double tap to choose which dashboard to view"
                >
                  <View style={styles.dashboardSwitcherText}>
                    <Text style={styles.dashboardSwitcherTitle} numberOfLines={2}>
                      {selectedDashboardTitle}
                    </Text>
                    <Text style={styles.dashboardSwitcherSubtitle} numberOfLines={2}>
                      {selectedDashboardSubtitle}
                    </Text>
                  </View>
                  <Text style={styles.dashboardSwitcherChevron}>v</Text>
                </TouchableOpacity>
              </View>
            )}
            <Text style={styles.pageTitle}>EOL Checklists</Text>
          </View>
          {isViewingOthersData && (
            <View style={styles.banner} accessibilityRole="alert" accessibilityLabel={`Viewing ${availablePrimaryUsers.find(u => u.id === selectedUserId)?.name}'s Data as ${availablePrimaryUsers.find(u => u.id === selectedUserId)?.role}`}>
              <Text style={styles.bannerText}>
                Viewing {availablePrimaryUsers.find(u => u.id === selectedUserId)?.name}'s Data as {availablePrimaryUsers.find(u => u.id === selectedUserId)?.role}
              </Text>
            </View>
          )}
        </View>
          {/* My Priorities Button - prominent access to personalized task list */}
          {!isViewingOthersData && (
            <TouchableOpacity
              style={styles.prioritiesButton}
              onPress={() => navigation.navigate('Profile', { screen: 'MyPriorities' })}
              activeOpacity={0.8}
              accessibilityRole="button"
              accessibilityLabel="My Task Priorities"
              accessibilityHint="Double tap to view your personalized task recommendations"
            >
              <View style={styles.prioritiesButtonContent}>
                <Text style={styles.prioritiesButtonIcon} accessibilityElementsHidden={true}>⭐</Text>
                <View style={styles.prioritiesButtonTextContainer}>
                  <Text style={styles.prioritiesButtonTitle}>My Task Priorities</Text>
                  <Text style={styles.prioritiesButtonSubtitle}>View your personalized task recommendations</Text>
                </View>
                <Text style={styles.prioritiesButtonArrow} accessibilityElementsHidden={true}>›</Text>
              </View>
            </TouchableOpacity>
          )}

          {!isViewingOthersData && <LettersQuickAccessCard />}

          <SuccessorPromptBanner />
          <PendingConnectionsBanner />

          {isViewingOthersData && selectedUserId && (
            <WellnessRequestCard
              primaryUserId={selectedUserId}
              primaryName={availablePrimaryUsers.find(u => u.id === selectedUserId)?.name}
            />
          )}

          <View style={styles.cardsContainer} accessibilityRole="list" accessibilityLabel="EOL Checklists">
            {checklists.map((checklist, index) => (
              <View key={checklist.id} style={styles.cardWrapper} accessibilityRole="none">
                <View style={[
                  styles.card,
                  isViewingOthersData && styles.orangeCard
                ]}>
                  <Text style={[
                    styles.cardTitle,
                    isViewingOthersData && styles.orangeText
                  ]} accessibilityRole="header">
                    {checklist.title}
                  </Text>
                  {checklist.id === 'passwords' ? (
                    // Special layout for passwords card
                    <View style={styles.passwordsStatusRow}>
                      <View style={styles.passwordsStatusColumn}>
                        <Text style={styles.statusLabel}>Total</Text>
                        <Text style={styles.statusCount}>{passwordCount} passwords</Text>
                      </View>
                    </View>
                  ) : checklist.id === 'contacts' ? (
                    // Special layout for contacts card
                    <View style={styles.passwordsStatusRow}>
                      <View style={styles.passwordsStatusColumn}>
                        <Text style={styles.statusLabel}>Total</Text>
                        <Text style={styles.statusCount}>{contactCount} contacts</Text>
                      </View>
                    </View>
                  ) : (
                    // Default layout for other cards
                    <View style={styles.statusRow}>
                      <View style={styles.statusColumn}>
                        <Text style={styles.statusLabel}>Total</Text>
                        <Text style={styles.statusCount}>{checklist.todo} Tasks</Text>
                      </View>
                      <View style={styles.statusColumn}>
                        <Text style={styles.statusLabel}>Done</Text>
                        <Text style={styles.statusCount}>{checklist.done} Tasks</Text>
                      </View>
                    </View>
                  )}
                  {(() => {
                    const { text, style, textStyle } = getButtonProps(checklist.todo, checklist.done, checklist.id);
                    const progressText = checklist.id === 'passwords'
                      ? `${passwordCount} passwords`
                      : checklist.id === 'contacts'
                      ? `${contactCount} contacts`
                      : `${checklist.done} of ${checklist.todo} tasks completed`;
                    return (
                      <TouchableOpacity
                        style={[styles.button, style]}
                        onPress={() => handleCardPress(checklist.id)}
                        accessibilityRole="button"
                        accessibilityLabel={`${checklist.title}: ${text}`}
                        accessibilityHint={`${progressText}. Double tap to ${text.toLowerCase()}`}
                      >
                        <Text style={[styles.buttonText, textStyle]}>{text}</Text>
                      </TouchableOpacity>
                    );
                  })()}
                </View>
              </View>
            ))}
          </View>
        </ScrollView>
      </View>
      <Modal
        visible={isDashboardSwitcherOpen}
        transparent={true}
        animationType="fade"
        statusBarTranslucent={true}
        onRequestClose={() => setIsDashboardSwitcherOpen(false)}
      >
        <View style={styles.dashboardDropdownOverlay}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={() => setIsDashboardSwitcherOpen(false)}
            accessibilityRole="button"
            accessibilityLabel="Close dashboard selector"
          />
          <View style={styles.dashboardDropdownMenu}>
            <Text style={styles.dashboardDropdownTitle}>Choose Dashboard</Text>
            <Text style={styles.dashboardDropdownDescription}>
              Switch between your dashboard and the primary user dashboards you can access.
            </Text>
            <ScrollView
              style={styles.dashboardDropdownList}
              contentContainerStyle={styles.dashboardDropdownListContent}
              showsVerticalScrollIndicator={availablePrimaryUsers.length > 3}
            >
              <TouchableOpacity
                style={[
                  styles.dashboardDropdownOption,
                  !selectedPrimaryUser && styles.dashboardDropdownOptionSelected,
                ]}
                onPress={() => selectDashboardUser(currentUserId)}
                accessibilityRole="button"
                accessibilityLabel="My Dashboard"
              >
                <View style={styles.dashboardDropdownOptionText}>
                  <Text style={styles.dashboardDropdownOptionTitle}>My Dashboard</Text>
                  <Text style={styles.dashboardDropdownOptionSubtitle} numberOfLines={2}>
                    {ownDashboardName || userProfile?.email || 'Your information'}
                  </Text>
                </View>
                {!selectedPrimaryUser && (
                  <Text style={styles.dashboardDropdownSelectedText}>Selected</Text>
                )}
              </TouchableOpacity>

              {availablePrimaryUsers.map((user) => {
                const isSelected = selectedUserId === user.id;

                return (
                  <TouchableOpacity
                    key={user.id}
                    style={[
                      styles.dashboardDropdownOption,
                      isSelected && styles.dashboardDropdownOptionSelected,
                    ]}
                    onPress={() => selectDashboardUser(user.id)}
                    accessibilityRole="button"
                    accessibilityLabel={`${user.name}'s Dashboard, ${user.role === 'successor' ? 'Successor' : 'Key Holder'}`}
                  >
                    <View style={styles.dashboardDropdownOptionText}>
                      <Text style={styles.dashboardDropdownOptionTitle} numberOfLines={2}>
                        {user.name}'s Dashboard
                      </Text>
                      <Text style={styles.dashboardDropdownOptionSubtitle} numberOfLines={2}>
                        {user.role === 'successor' ? 'Successor access' : 'Key Holder access'} - {user.email}
                      </Text>
                    </View>
                    {isSelected && (
                      <Text style={styles.dashboardDropdownSelectedText}>Selected</Text>
                    )}
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
      </Modal>
      {/* Side Menu Overlay */}
      {menuOpen && (
        <TouchableOpacity
          style={styles.menuOverlay}
          activeOpacity={1}
          onPress={() => setMenuOpen(false)}
          accessibilityRole="button"
          accessibilityLabel="Close menu overlay"
          accessibilityHint="Double tap to close the navigation menu"
        >
          <View style={styles.sideMenu} accessibilityRole="menu" accessibilityLabel="Navigation menu">
            <Image source={logoWhite} style={styles.menuLogo} resizeMode="contain" accessibilityElementsHidden={true} />
            <TouchableOpacity
              onPress={() => setMenuOpen(false)}
              style={styles.closeMenuButton}
              accessibilityRole="button"
              accessibilityLabel="Close menu"
              accessibilityHint="Double tap to close the navigation menu"
            >
              <Text style={styles.closeMenuText} accessibilityElementsHidden={true}>×</Text>
            </TouchableOpacity>
            <View style={styles.menuLinksContainer} accessibilityRole="list">
              <TouchableOpacity
                style={styles.menuLink}
                onPress={() => { setMenuOpen(false); navigation.navigate('Profile', { screen: 'ProfileDetails' }); }}
                accessibilityRole="menuitem"
                accessibilityLabel="My Profile"
                accessibilityHint="Double tap to view your profile"
              >
                <Image source={userIcon} style={styles.menuIcon} accessibilityElementsHidden={true} />
                <Text style={styles.menuItem}>My Profile</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.menuLink}
                onPress={() => { setMenuOpen(false); navigation.navigate('AllTasks'); }}
                accessibilityRole="menuitem"
                accessibilityLabel="All Tasks"
                accessibilityHint="Double tap to view all tasks"
              >
                <Image source={tasksIcon} style={styles.menuIcon} accessibilityElementsHidden={true} />
                <Text style={styles.menuItem}>All Tasks</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.menuLink}
                onPress={() => { setMenuOpen(false); navigation.navigate('Documents'); }}
                accessibilityRole="menuitem"
                accessibilityLabel="My Documents"
                accessibilityHint="Double tap to view your documents"
              >
                <Image source={docsIcon} style={styles.menuIcon} accessibilityElementsHidden={true} />
                <Text style={styles.menuItem}>My Documents</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.menuLink}
                onPress={() => { setMenuOpen(false); navigation.reset({ index: 0, routes: [{ name: 'Dashboard' }] }); }}
                accessibilityRole="menuitem"
                accessibilityLabel="Dashboard"
                accessibilityHint="Double tap to go to the dashboard"
              >
                <Image source={dashboardIcon} style={styles.menuIcon} accessibilityElementsHidden={true} />
                <Text style={styles.menuItem}>Dashboard</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.menuLink}
                onPress={() => { setMenuOpen(false); setFeedbackOpen(true); }}
                accessibilityRole="menuitem"
                accessibilityLabel="Send Feedback"
                accessibilityHint="Double tap to open the feedback form"
              >
                <Image source={feedbackIcon} style={styles.menuIcon} accessibilityElementsHidden={true} />
                <Text style={styles.menuItem}>Feedback</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.menuLink}
                onPress={() => { setMenuOpen(false); handleLogout(); }}
                accessibilityRole="menuitem"
                accessibilityLabel="Logout"
                accessibilityHint="Double tap to sign out"
              >
                <Image source={logoutIcon} style={styles.menuIcon} accessibilityElementsHidden={true} />
                <Text style={styles.menuItem}>Logout</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      )}
      <FeedbackModal visible={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    paddingTop: ms(15),
  },
  mainContent: {
    flex: 1,
  },
  header: {
    padding: ms(20),
    paddingBottom: ms(10),
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(20),
    position: 'relative',
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  logo: {
    height: ms(150, 0.3),
    width: ms(150, 0.3),
  },
  notificationButton: {
    position: 'absolute',
    right: 0,
    top: ms(45),
    padding: ms(5),
  },
  bellIcon: {
    width: ms(30, 0.3),
    height: ms(30, 0.3),
    tintColor: '#333',
  },
  badgeContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: '#FF3B30',
    borderRadius: ms(10),
    minWidth: ms(20, 0.3),
    // minHeight, not height: this is the one container on this screen
    // that wraps text ("99+"), so a fixed height clips it as soon as
    // the badge count or the user's text size grows.
    minHeight: ms(20, 0.3),
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(5),
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: ms(11, 0.3),
    fontWeight: 'bold',
  },
  welcomeSection: {
    marginBottom: ms(15),
  },
  profileSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  profileImageContainer: {
    width: ms(75, 0.3),
    height: ms(75, 0.3),
    borderRadius: ms(37.5),
    overflow: 'hidden',
    marginRight: ms(15),
  },
  profileImage: {
    width: ms(75, 0.3),
    height: ms(75, 0.3),
    borderRadius: ms(37.5),
  },
  welcomeText: {
    justifyContent: 'center',
  },
  welcomeBack: {
    fontSize: ms(14, 0.3),
    color: '#666',
  },
  userName: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  pageTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginTop: ms(15),
  },
  scrollView: {
    flex: 1,
  },
  cardsContainer: {
    padding: ms(10),
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  cardWrapper: {
    width: '48%',
    marginBottom: ms(15),
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(10),
    elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
  },
  highlightedCard: {
    backgroundColor: '#29BFC0',
    borderColor: '#29BFC0',
  },
  completedCard: {
    backgroundColor: '#6A89A7',
    borderColor: '#FF9500',
  },
  cardTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(15),
    textAlign: 'center',
    minHeight: ms(52, 0.3),
  },
  highlightedText: {
    color: '#FFFFFF',
  },
  completedText: {
    color: '#FFFFFF',
  },
  statusRow: {
    flexDirection: 'row',
    marginBottom: ms(20),
    justifyContent: 'space-evenly',
  },
  statusColumn: {
    // flex so the two columns split the card evenly and can compress on
    // narrow screens. Without this they size to their text and overflow.
    flex: 1,
    alignItems: 'center',
  },
  passwordsStatusRow: {
    flexDirection: 'row',
    marginBottom: ms(20),
    justifyContent: 'center',
    paddingHorizontal: ms(10),
  },
  passwordsStatusColumn: {
    alignItems: 'center',
  },
  statusLabel: {
    fontSize: ms(16, 0.3),
    color: '#666',
    marginBottom: ms(5),
    textAlign: 'center',
  },
  statusCount: {
    fontSize: ms(16, 0.3),
    color: '#333',
    fontWeight: '600',
    textAlign: 'center',
  },
  buttonContainer: {
    alignItems: 'center',
  },
  button: {
    backgroundColor: '#F0F0F0',
    borderRadius: ms(20),
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    width: '100%',
    alignItems: 'center',
    elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
    marginBottom: ms(4),
  },
  getStartedButton: {
    backgroundColor: '#29BFC0',
  },
  getStartedButtonText: {
    color: '#fff',
  },
  continueButton: {
    backgroundColor: '#6A89A7',
  },
  continueButtonText: {
    color: '#FFFFFF',
  },
  reviewButton: {
    backgroundColor: '#F2994A',
  },
  reviewButtonText: {
    color: '#FFFFFF',
  },
  viewPasswordsButton: {
    backgroundColor: '#F2994A',
  },
  viewPasswordsButtonText: {
    color: '#FFFFFF',
  },
  viewContactsButton: {
    backgroundColor: '#F2994A',
  },
  viewContactsButtonText: {
    color: '#FFFFFF',
  },
  viewDataButton: {
    backgroundColor: '#29BFC0',
  },
  viewDataButtonText: {
    color: '#FFFFFF',
  },
  highlightedButton: {
    backgroundColor: '#FFFFFF',
  },
  completedButton: {
    backgroundColor: '#FFFFFF',
  },
  buttonText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
  myDataButton: {
    backgroundColor: '#F2994A',
    marginTop: ms(-5),
    height: ms(25, 0.3),
    justifyContent: 'center',
    paddingVertical: 0,
    marginBottom: ms(2),
    width: '70%',
    alignSelf: 'center',
  },
  myDataButtonText: {
    color: '#EFF2F5',
    fontWeight: 'bold',
    lineHeight: ms(25, 0.3),
  },
  dashboardSwitcherSection: {
    marginTop: ms(15),
    marginBottom: ms(10),
  },
  dashboardSwitcherLabel: {
    fontSize: ms(13, 0.3),
    fontWeight: '700',
    color: '#4B5563',
    marginBottom: ms(6),
  },
  dashboardSwitcherButton: {
    minHeight: ms(66, 0.3),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    paddingVertical: ms(10),
    paddingHorizontal: ms(14),
    borderWidth: 1,
    borderColor: '#D8DBDD',
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  dashboardSwitcherText: {
    flex: 1,
    paddingRight: ms(10),
  },
  dashboardSwitcherTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    lineHeight: ms(21, 0.3),
  },
  dashboardSwitcherSubtitle: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    lineHeight: ms(18, 0.3),
    marginTop: ms(2),
  },
  dashboardSwitcherChevron: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#29BFC0',
  },
  dashboardDropdownOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(20),
    paddingVertical: ms(24),
  },
  dashboardDropdownMenu: {
    width: '100%',
    maxWidth: s(420),
    maxHeight: '78%',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    padding: ms(18),
  },
  dashboardDropdownTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    marginBottom: ms(6),
  },
  dashboardDropdownDescription: {
    fontSize: ms(13, 0.3),
    color: '#6B7280',
    lineHeight: ms(18, 0.3),
    marginBottom: ms(12),
  },
  dashboardDropdownList: {
    flexGrow: 0,
  },
  dashboardDropdownListContent: {
    paddingBottom: ms(2),
  },
  dashboardDropdownOption: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: ms(10),
    paddingVertical: ms(12),
    paddingHorizontal: ms(12),
    marginBottom: ms(10),
    flexDirection: 'row',
    alignItems: 'center',
  },
  dashboardDropdownOptionSelected: {
    borderColor: '#29BFC0',
    backgroundColor: '#EAFBFB',
  },
  dashboardDropdownOptionText: {
    flex: 1,
    paddingRight: ms(8),
  },
  dashboardDropdownOptionTitle: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: '#1C2C4F',
    lineHeight: ms(20, 0.3),
  },
  dashboardDropdownOptionSubtitle: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
    lineHeight: ms(17, 0.3),
    marginTop: ms(3),
  },
  dashboardDropdownSelectedText: {
    fontSize: ms(12, 0.3),
    fontWeight: '700',
    color: '#29BFC0',
  },
  banner: {
    backgroundColor: '#F2994A',
    borderRadius: ms(10),
    padding: ms(8),
    marginTop: ms(10),
    marginBottom: ms(10),
    alignItems: 'center',
  },
  bannerText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: ms(15, 0.3),
  },
  orangeCard: {
    backgroundColor: '#F2994A',
  },
  orangeText: {
    color: '#fff',
  },
  orangeViewButton: {
    backgroundColor: '#fff',
    marginTop: ms(10),
  },
  orangeViewButtonText: {
    color: '#F2994A',
    fontWeight: 'bold',
  },
  fab: {
    position: 'absolute',
    right: ms(25),
    bottom: ms(35),
    backgroundColor: '#F2994A',
    borderRadius: ms(30),
    paddingVertical: ms(16),
    paddingHorizontal: ms(28),
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    zIndex: 100,
  },
  fabText: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: ms(18, 0.3),
    letterSpacing: 1,
  },
  hamburgerButton: {
    position: 'absolute',
    left: ms(45),
    top: ms(10),
    zIndex: 20,
    width: ms(40, 0.3),
    height: ms(40, 0.3),
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  hamburgerBar: {
    width: ms(28, 0.3),
    height: ms(4, 0.3),
    backgroundColor: '#1C2C4F',
    borderRadius: ms(2),
    marginVertical: ms(2),
  },
  menuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.2)',
    zIndex: 1000,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  sideMenu: {
    width: ms(260, 0.3),
    height: '98%',
    backgroundColor: '#1C2C4F',
    borderTopRightRadius: ms(30),
    borderBottomRightRadius: ms(30),
    paddingTop: ms(40),
    paddingHorizontal: ms(20),
    shadowColor: '#000',
    shadowOffset: { width: 2, height: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    marginTop: ms(5),
    marginBottom: ms(5),
  },
  closeMenuButton: {
    position: 'absolute',
    top: ms(10),
    right: ms(10),
    zIndex: 10,
  },
  closeMenuText: {
    color: '#fff',
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
  },
  menuItem: {
    color: '#fff',
    fontSize: ms(20, 0.3),
    marginVertical: ms(18),
    fontWeight: '500',
  },
  menuLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: ms(18),
  },
  menuIcon: {
    width: ms(28, 0.3),
    height: ms(28, 0.3),
    marginRight: ms(14),
    resizeMode: 'contain',
    tintColor: '#29BFC0',
  },
  menuLogo: {
    width: ms(120, 0.3),
    height: ms(120, 0.3),
    alignSelf: 'center',
    marginTop: ms(10),
    marginBottom: ms(20),
  },
  menuLinksContainer: {
    marginTop: ms(10),
  },
  profileImagePlaceholder: {
    width: ms(75, 0.3),
    height: ms(75, 0.3),
    borderRadius: ms(37.5),
    overflow: 'hidden',
    marginRight: ms(15),
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileImageIcon: {
    width: ms(75, 0.3),
    height: ms(75, 0.3),
    tintColor: '#29BFC0',
  },
  prioritiesButton: {
    backgroundColor: '#29BFC0',
    marginHorizontal: ms(15),
    marginTop: ms(10),
    marginBottom: ms(15),
    borderRadius: ms(15),
    padding: ms(16),
    shadowColor: '#29BFC0',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 5,
  },
  prioritiesButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  prioritiesButtonIcon: {
    fontSize: ms(24, 0.3),
    marginRight: ms(12),
  },
  prioritiesButtonTextContainer: {
    flex: 1,
  },
  prioritiesButtonTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  prioritiesButtonSubtitle: {
    fontSize: ms(12, 0.3),
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: ms(2),
  },
  prioritiesButtonArrow: {
    fontSize: ms(28, 0.3),
    color: '#FFFFFF',
    fontWeight: '300',
  },
});

export default DashboardScreen;
